import {
    CustomTable,
    CustomTableButtonWrapper,
    CustomTd,
    DashboardPageContent,
    FormWrapper,
    PageTitle,
} from 'custom-style-components';
import { DeleteIcon, EditIcon, LockIcon } from '@chakra-ui/icons';
import { IconButton, Switch, Tooltip, useToast } from '@chakra-ui/react';
import { Link, useRouteMatch } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

import COLORS from 'constants/COLORS';
import ConfirmDeleteComponent from 'components/ConfirmDeleteComponent';
import ConfirmReenableComponent from 'components/ConfirmReenableComponent';
import CustomButton from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import { GenericFrontError } from 'errors/GenericFrontError';
import LoadingSpinner from 'components/LoadingSpinner';
import { People } from 'types/models';
import { ResponseError } from 'errors/ResponseError';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyUsers } from 'services/fetch/users';
import { maskCpf } from 'utils/maskCpf';

const ListUsers: React.FC = () => {
    const toast = useToast({
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const [userList, setUserList] = useState<People[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [userShow, setUserShow] = useState<People[]>([]);

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(-1);

    const [confirmDisableModalOpen, setConfirmDisableModalOpen] =
        useState(false);
    const [idToDisable, setIdToDisable] = useState(-1);

    const [confirmReenableModalOpen, setConfirmReenableModalOpen] =
        useState(false);
    const [idToReenable, setIdToReenable] = useState(-1);

    const { path } = useRouteMatch();

    const source = useRef(createCancelTokenSource());

    const loadUsers = () => {
        source.current = createCancelTokenSource();
        setLoading(true);
        fetchManyUsers(source.current.token, {
            limit: Number.MAX_SAFE_INTEGER,
        })
            .then(({ users }) => {
                setUserList(users);
                setUserShow(users);
            })
            .catch((err) => {
                setError(err.message);
                toast({
                    title: err.message,
                    status: 'error',
                });
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadUsers();
        return () => source.current.cancel();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        if (!userList) return;
        const lowerCaseUserName = debouncedSearchTerm.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        
        const filteredUsers = userList.filter((user) => {
            const lowerCaseName = user.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            
            if (
                user.email &&
                user.email.toLowerCase().includes(lowerCaseUserName)
            ) {
                return true;
            }

            if (
                user.cpf &&
                user.cpf.toLowerCase().includes(lowerCaseUserName)
            ) {
                return true;
            }

            return lowerCaseName.includes(lowerCaseUserName);
        });

        setUserShow(filteredUsers);
    }, [debouncedSearchTerm, userList]);

    const deleteUser = async (id: number) => {
        try {
            await api.delete(`/user/${id}`);

            if (userList) {
                setUserList([...userList.filter((user) => user.id != id)]);
            }

            toast({
                title: 'Usuário deletado',
                status: 'success',
            });
            loadUsers();
        } catch (err) {
            let message = 'Erro não tratado';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError) {
                    if (err.status === 408)
                        message =
                            'Esse usuário está ativo no momento, para deletá-lo, desative-o primeiro';
                    if (err.status === 400)
                        message =
                            'Esse usuário organiza um evento ou uma atividade';
                }
            }
            toast({
                title: message,
                status: 'error',
            });
        } finally {
            setConfirmDeleteModalOpen(false);
        }
    };

    const disableUser = async (id: number) => {
        try {
            await api.delete(`/user/disable/${id}`);

            if (userList) {
                setUserList([...userList.filter((user) => user.id != id)]);
            }

            toast({
                title: 'Usuário desativado',
                status: 'success',
            });
            loadUsers();
        } catch (err) {
            let message = 'Erro não tratado';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError) {
                    if (err.status === 409)
                        message =
                            'Esse usuário está organizando um evento ou uma atividade no momento';
                }
            }
            toast({
                title: message,
                status: 'error',
            });
        } finally {
            setConfirmDisableModalOpen(false);
        }
    };

    const reenableUser = async (id: number) => {
        try {
            await api.post(`/user/reenable/${id}`);

            if (userList) {
                setUserList([...userList.filter((user) => user.id != id)]);
            }

            toast({
                title: 'Usuário reativado',
                status: 'success',
            });
            loadUsers();
        } catch (err) {
            let message = 'Erro não tratado';
            if (err instanceof GenericFrontError) {
                message = err.message;
                if (err instanceof ResponseError) {
                    if (err.status === 409)
                        message =
                            'Esse usuário não pode ser reativado pois já está ativo';
                }
            }
            toast({
                title: message,
                status: 'error',
            });
        } finally {
            setConfirmReenableModalOpen(false);
        }
    };

    const tableElements = React.useMemo(() => {
        return userShow && userShow.length > 0 ? (
            userShow.map((user) => {
                return (
                    <tr key={user.id}>
                        <CustomTd>{user.name}</CustomTd>
                        <CustomTd align="center">
                            {user.cpf && maskCpf(user.cpf)}
                        </CustomTd>
                        <CustomTd align="center">{user.email}</CustomTd>
                        <CustomTd>
                            <CustomTableButtonWrapper>
                                <Tooltip
                                    hasArrow
                                    fontSize="1.2rem"
                                    label="Trocar senha do usuário"
                                >
                                    <IconButton
                                        variant="ghost"
                                        fontSize="1.5rem"
                                        fontWeight="bold"
                                        color={COLORS.primaryContrast}
                                        as={Link}
                                        icon={<LockIcon />}
                                        aria-label="Trocar senha do usuário"
                                        to={path + `/${user.id}/alterar_senha`}
                                    />
                                </Tooltip>
                                
                            </CustomTableButtonWrapper>
                        </CustomTd>
                    </tr>
                );
            })
        ) : (
            <tr key="empty">
                <td colSpan={4}>
                    <ErrorMessage>Nenhum resultado encontrado.</ErrorMessage>
                </td>
            </tr>
        );
    }, [userShow, path]);

    return (
        <DashboardPageContent>
            <FormWrapper>
                <PageTitle>Usuários</PageTitle>
                {userList && userList.length > 0 && (
                    <div
                        style={{
                            justifyContent: 'left',
                            margin: '2rem auto 3rem',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <label htmlFor="search" style={{ marginRight: '1rem', fontWeight: 'bold' }}>Pesquisar usuário</label>
                        <input
                            id="search"
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Pesquise por Nome, E-mail ou CPF."
                            style={{
                                borderRadius: '5px',
                                borderColor: '#bebebe',
                                width: '50%',
                                fontSize: 'max(16px,1em)',
                                backgroundColor: '#fff',
                                minHeight: '3.8rem',
                                padding: '0 1rem',
                            }}
                        />
                    </div>
                )}

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : userShow && userShow.length > 0 ? (
                    <>
                        <CustomTable>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>CPF</th>
                                    <th>E-mail</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>{!loading && tableElements}</tbody>
                        </CustomTable>
                        {loading && <LoadingSpinner />}
                    </>
                ) : loading ? (
                    <LoadingSpinner />
                ) : (
                    <ErrorMessage>Nenhum resultado encontrado.</ErrorMessage>
                )}
            </FormWrapper>
        </DashboardPageContent >
    );
};

export default ListUsers;
